import { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { addLanguageToPath, getLanguageFromPath } from "../../languages";
import {
  CoursesNavItem,
  CoursesDropdown,
  CoursesDropdownPanel,
  CoursesDropdownAnchor,
  CoursesDropdownLink,
  CoursesMobileList,
  CoursesMobileExpandButton,
  CoursesMobileTopRow,
  CoursesMobileToggle,
  CoursesMobileSubmenu,
  CoursesMobileSubAnchor,
  CoursesMobileSubLink,
} from "./styles";

type ResourcesNavProps = {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
  drawerOpen?: boolean;
};

const BLOG_URL = "https://publicspeakingcoach.app/blog";

const ResourcesTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  .anticon { font-size: 0.6em; }
  &:hover { color: #c14f31; }
  &:focus-visible { outline: 2px solid #c14f31; outline-offset: 5px; }
`;

const ResourcesNav = ({
  variant = "desktop",
  onNavigate,
  drawerOpen = false,
}: ResourcesNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const podcastPath = addLanguageToPath("/podcast", getLanguageFromPath(location.pathname));
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  useEffect(() => {
    if (!drawerOpen) {
      setMobileExpanded(false);
    }
  }, [drawerOpen]);

  const handleNavigate = () => {
    setDesktopOpen(false);
    setMobileExpanded(false);
    onNavigate?.();
  };

  if (variant === "mobile") {
    return (
      <CoursesMobileList>
        <CoursesMobileTopRow>
          <CoursesMobileToggle
            type="button"
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            {t("resources.nav")}
          </CoursesMobileToggle>
          <CoursesMobileExpandButton
            type="button"
            aria-label={t("resources.mobile.toggle")}
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            <DownOutlined />
          </CoursesMobileExpandButton>
        </CoursesMobileTopRow>
        {mobileExpanded && (
          <CoursesMobileSubmenu>
            <CoursesMobileSubLink to={podcastPath} onClick={handleNavigate}>
              {t("podcast.nav")}
            </CoursesMobileSubLink>
            <CoursesMobileSubAnchor href={BLOG_URL} onClick={handleNavigate}>
              {t("Blog")}
            </CoursesMobileSubAnchor>
          </CoursesMobileSubmenu>
        )}
      </CoursesMobileList>
    );
  }

  return (
    <CoursesNavItem
      onMouseEnter={() => setDesktopOpen(true)}
      onMouseLeave={() => setDesktopOpen(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDesktopOpen(false);
      }}
      onKeyDown={(event) => { if (event.key === "Escape") setDesktopOpen(false); }}
    >
      <ResourcesTrigger
        type="button"
        aria-expanded={desktopOpen}
        aria-controls="resources-dropdown"
        onClick={() => setDesktopOpen((open) => !open)}
      >
        {t("resources.nav")}
        <DownOutlined aria-hidden="true" />
      </ResourcesTrigger>
      <CoursesDropdown id="resources-dropdown" $open={desktopOpen}>
        <CoursesDropdownPanel>
          <CoursesDropdownLink to={podcastPath} onClick={handleNavigate}>
            {t("podcast.nav")}
          </CoursesDropdownLink>
          <CoursesDropdownAnchor href={BLOG_URL} onClick={handleNavigate}>
            {t("Blog")}
          </CoursesDropdownAnchor>
        </CoursesDropdownPanel>
      </CoursesDropdown>
    </CoursesNavItem>
  );
};

export default ResourcesNav;
